import { Modal, Tab } from "@wrstudios/components";
import { IconHome } from "@wrstudios/icons";
import { formatCurrency } from "@wrstudios/utils";
import React, { useState } from "react";
import Helmet from "react-helmet";
import {
  getAverageAdjustedPricePerSqft,
  getAverageAdjustedPricePerSqftByLotSize
} from "../../../utils/listings";
import Adjustment from "../../common/Adjustment";
import Wistia from "../../common/Wistia";
import IconLot from "../../icons/IconLot";
import AgentNote from "../common/AgentNote";
import LiveHeader from "../common/LiveHeader";
import IconInfo from "../icons/IconInfo";
import LiveListingPreview, {
  useListingPreviewResizer
} from "../listing/LiveListingPreview";
import { useLive } from "../LiveProvider";
import LiveAveragePricePerAcreChart from "./LiveAveragePricePerAcreChart";
import LiveAveragePricePerSqftChart from "./LiveAveragePricePerSqftChart";
import LiveListings from "./LiveListings";
import {
  Container,
  Content,
  Description,
  Info,
  ModalContent,
  ModalText,
  Sidebar,
  SubTitle,
  Title,
  TitleContainer,
  Value
} from "./styled/live-average-price-per-sqft";

function LiveAveragePricePerSqft() {
  const { visibleListings, subjectProperty, getModuleConfig } = useLive();
  const { headerRef, mobileHeaderRef, previewRef, headerDetails } =
    useListingPreviewResizer();
  const [previewListingId, setPreviewListingId] = useState(null);
  const [hoverListingId, setHoverListingId] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [graphType, setGraphType] = useState("home");
  const soldListings = visibleListings.filter((listing) => listing.soldPrice);
  const averageAdjustedSoldPricePerSqft =
    getAverageAdjustedPricePerSqft(soldListings);

  const averageAdjustedSoldPricePerSqftByLotSize =
    getAverageAdjustedPricePerSqftByLotSize(soldListings);

  const updateUserTabSelection = (selection) => {
    setGraphType(selection.value);
  };

  const pageConfig = getModuleConfig("average-price-per-sqft");

  return (
    <>
      <Helmet>
        <title>Average Price Per Sqft</title>
      </Helmet>
      <LiveHeader headerRef={headerRef} mobileHeaderRef={mobileHeaderRef}>
        Average Price/Sq. Ft.
      </LiveHeader>
      <Container>
        <Sidebar>
          <LiveListings
            previewListingId={previewListingId}
            setPreviewListingId={setPreviewListingId}
            setHoverListingId={setHoverListingId}
            listingFormat={(listing) => (
              <>
                {listing.pricePerSqft ? (
                  <Adjustment
                    value={listing.pricePerSqft}
                    adjustedValue={listing.adjustedPricePerSqft}
                    shouldDim={false}
                  />
                ) : (
                  "$---"
                )}{" "}
                / sq. ft.
              </>
            )}
          />
        </Sidebar>
        <Content>
          <AgentNote anchor="top" note={pageConfig.note} />
          <TitleContainer>
            <Title className="font-primary page-title">
              {graphType === "home" && averageAdjustedSoldPricePerSqft
                ? formatCurrency(
                    averageAdjustedSoldPricePerSqft,
                    averageAdjustedSoldPricePerSqft < 1
                      ? "$0[.]0[0]0"
                      : undefined
                  )
                : graphType === "lot" &&
                  averageAdjustedSoldPricePerSqftByLotSize
                ? formatCurrency(
                    averageAdjustedSoldPricePerSqftByLotSize,
                    averageAdjustedSoldPricePerSqftByLotSize < 1
                      ? "$0[.]0[0]0"
                      : undefined
                  )
                : "$---"}
              <SubTitle className="page-sub-title">
                {" "}
                /{" "}
                {graphType === "lot" &&
                subjectProperty.landMeasurement === "acres"
                  ? "Acre"
                  : "Sq. Ft."}{" "}
              </SubTitle>
            </Title>
            <Tab
              parentHandler={(e) => updateUserTabSelection(e)}
              tabList={[
                { value: "home", text: "Home", icon: <IconHome /> },
                { value: "lot", text: "Lot", icon: <IconLot /> }
              ]}
              type="pills"
              activeColor="#053EA2"
              activeTab="home"
              rightAlign
            />
          </TitleContainer>
          {!!averageAdjustedSoldPricePerSqft && (
            <Description className="page-description">
              Comparable homes sold for an average of{" "}
              <Value className="page-description-value">
                {graphType === "home" && averageAdjustedSoldPricePerSqft
                  ? formatCurrency(
                      averageAdjustedSoldPricePerSqft,
                      averageAdjustedSoldPricePerSqft < 1
                        ? "$0[.]0[0]0"
                        : undefined
                    )
                  : graphType === "lot" &&
                    averageAdjustedSoldPricePerSqftByLotSize
                  ? formatCurrency(
                      averageAdjustedSoldPricePerSqftByLotSize,
                      averageAdjustedSoldPricePerSqftByLotSize < 1
                        ? "$0[.]0[0]0"
                        : undefined
                    )
                  : "$---"}
              </Value>{" "}
              /{" "}
              {graphType === "lot" &&
              subjectProperty.landMeasurement === "acres"
                ? "acre"
                : "sq. ft."}{" "}
              Many factors such as location, use of space, condition, quality,
              and amenities determine the market value per square foot, so
              reviewing each comp carefully is important.
              <Info onClick={() => setShowInfoModal(true)}>
                <IconInfo />
              </Info>
            </Description>
          )}
          {!averageAdjustedSoldPricePerSqft && (
            <Description className="page-description">
              To view the average price/sq. ft. of sold listings, please add
              sold comparables to this report.
              <Info onClick={() => setShowInfoModal(true)}>
                <IconInfo />
              </Info>
            </Description>
          )}

          {graphType === "lot" &&
          subjectProperty.landMeasurement === "acres" ? (
            <LiveAveragePricePerAcreChart
              hoverListingId={hoverListingId}
              previewListingId={previewListingId}
              setPreviewListingId={setPreviewListingId}
            />
          ) : (
            <LiveAveragePricePerSqftChart
              graphType={graphType}
              hoverListingId={hoverListingId}
              previewListingId={previewListingId}
              setPreviewListingId={setPreviewListingId}
            />
          )}
        </Content>
        {previewListingId && (
          <LiveListingPreview
            ref={previewRef}
            style={{ top: headerDetails.bottom }}
            listingRecordId={previewListingId}
            showInModal={true}
            onClose={() => setPreviewListingId(null)}
          />
        )}
      </Container>
      {showInfoModal && (
        <Modal onClose={() => setShowInfoModal(false)}>
          <ModalContent>
            <Modal.Header className="font-primary modal-header">
              Price/Sq. Ft. Scatter Chart
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <ModalText className="modal-text">
                The price/sq. ft. scatter chart is a way to visualize the trend
                between the price and square footage of the home. A trendline
                will be drawn to show the correlation between price and square
                footage accross the sold listings. Watch this quick video clip
                on how to interact with this page.
              </ModalText>
              <Wistia videoId="xqe2n7zoqn" autoplay={false} />
            </Modal.Body>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}

export default LiveAveragePricePerSqft;
